<script setup lang="ts">
import { ref, computed } from 'vue'
import { useConfigurator } from '@/composables/configurator/useConfigurator'
import McpServerModal from '@/components/BusinessLogic/Modal/McpServerModal.vue'
import { useMcpServers } from '@/composables/configurator/useMcpServers'

const { t } = useI18n()
const { externalTools } = useConfigurator()

const mcpModalRef = ref<InstanceType<typeof McpServerModal>>()

const {
  canAddMcpServer,
  mcpDeleteIndex,
  mcpDeleteOpen,
  openAddMcpModal,
  handleMcpConfirm,
  confirmDeleteMcpServer,
  getMcpActions,
  formatToolTooltip,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
} = useMcpServers(externalTools, mcpModalRef)

const clampMaxToolCalls = () => {
  const v = externalTools.value.maxToolCallsPerTurn
  if (!Number.isFinite(v)) externalTools.value.maxToolCallsPerTurn = 5
  else if (v < 1) externalTools.value.maxToolCallsPerTurn = 1
  else if (v > 10) externalTools.value.maxToolCallsPerTurn = 10
}

const serverStatusDot = (server: { tools: { enabled?: boolean }[] }) =>
  server.tools.length > 0 ? 'bg-primary-success' : 'bg-neutral-4'

const deleteServerName = computed(() =>
  mcpDeleteIndex.value !== null ? externalTools.value.servers[mcpDeleteIndex.value]?.label : '',
)
</script>

<template>
  <div class="flex flex-col space-y-6" data-testid="tools-tab-content">
    <!-- MCP Servers -->
    <FormSection
      :title="t('tools.mcp.title')"
      :subtitle="t('tools.mcp.description')"
      data-testid="mcp-servers-section"
    >
      <div class="col-span-2 flex flex-col space-y-3">
        <div
          v-for="(server, index) in externalTools.servers"
          :key="index"
          class="p-3 border border-neutral-4 rounded-lg"
          :data-testid="`mcp-server-card-${index}`"
        >
          <div class="flex items-start justify-between">
            <div class="flex items-center gap-2 min-w-0 flex-1">
              <span class="size-2 rounded-full shrink-0" :class="serverStatusDot(server)" />
              <span class="typography-body-2-strong truncate">{{ server.label }}</span>
            </div>
            <OListActions
              small
              :actions="getMcpActions(index)"
              :data-testid="`mcp-server-actions-${index}`"
            />
          </div>
          <div v-if="server.tools.length" class="flex flex-wrap gap-1 mt-2">
            <OToolTip
              v-for="tool in server.tools"
              :key="tool.name"
              :content="formatToolTooltip(tool)"
            >
              <OBadge
                :color="tool.enabled === false ? 'neutral' : 'success'"
                :content="tool.name"
                variant="secondary"
              />
            </OToolTip>
          </div>
          <span v-else class="text-xs typography-body-1 block mt-2">
            {{ t('tools.mcp.empty') }}
          </span>
        </div>

        <OButton
          v-if="canAddMcpServer"
          variant="secondary"
          size="M"
          :label="t('tools.mcp.add')"
          @click="openAddMcpModal"
          dataTestid="add-mcp-server"
          class="self-start"
        />
      </div>
    </FormSection>

    <!-- Max tool calls per turn -->
    <FormSection
      :title="t('tools.mcp.maxToolCalls.title')"
      :subtitle="t('tools.mcp.maxToolCalls.description')"
      data-testid="max-tool-calls-section"
    >
      <div class="col-span-2">
        <OInput
          v-model.number="externalTools.maxToolCallsPerTurn"
          :label="t('tools.mcp.maxToolCalls.label')"
          type="number"
          :min="1"
          :max="10"
          size="M"
          :widthSize="2"
          @blur="clampMaxToolCalls"
          dataTestid="max-tool-calls-input"
        />
      </div>
    </FormSection>

    <McpServerModal ref="mcpModalRef" @confirm="handleMcpConfirm" />

    <OModal
      v-model:open="mcpDeleteOpen"
      danger
      :title="t('tools.mcp.deleteTitle')"
      :primary-action-title="t('delete_permanently')"
      :secondary-action-title="t('tools.mcp.drawer.cancel')"
      primary-action-enabled
      secondary-action-enabled
      :primary-action-promise="confirmDeleteMcpServer"
      :secondary-action-promise="
        () => {
          mcpDeleteOpen = false
          return true
        }
      "
      data-testid="mcp-delete-modal"
    >
      <template #content>
        <span class="typography-body-2 text-neutral-1">
          {{ t('tools.mcp.deleteConfirm', { name: deleteServerName }) }}
        </span>
      </template>
    </OModal>
  </div>
</template>
